import { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { getRouteTrelloMeta, type RouteTrelloMeta } from './routesTrelloMap';
import { LOCAL_BUILD_METADATA, type VersionManifest } from './localBuildMetadata';

export type { VersionManifest };
export { LOCAL_BUILD_METADATA };

export function useAppVersionTelemetry() {
  const location = useLocation();
  const [remoteManifest, setRemoteManifest] = useState<VersionManifest | null>(null);
  const [isStale, setIsStale] = useState<boolean>(false);
  const [lastCheck, setLastCheck] = useState<Date>(new Date());
  const [viewport, setViewport] = useState<{ width: number; height: number }>({
    width: typeof window !== 'undefined' ? window.innerWidth : 1280,
    height: typeof window !== 'undefined' ? window.innerHeight : 800,
  });
  const [isAuditVisible, setIsAuditVisible] = useState<boolean>(() => {
    try {
      const saved = localStorage.getItem('ac_audit_banner_enabled');
      return saved !== 'false'; // Padrão: Habilitado para visibilidade total
    } catch {
      return true;
    }
  });

  // Rastreia rota atual no catálogo do Trello
  const currentRouteMeta: RouteTrelloMeta = getRouteTrelloMeta(location.pathname);

  // Monitora redimensionamento de janela (Mobile vs Desktop)
  useEffect(() => {
    const handleResize = () => {
      setViewport({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Handshake com version-manifest.json no Firebase Hosting / CDN
  const checkRemoteVersion = useCallback(async () => {
    try {
      const response = await fetch(`/version-manifest.json?_t=${Date.now()}`, {
        cache: 'no-store',
        headers: { 'Cache-Control': 'no-cache, no-store' },
      });
      if (response.ok) {
        const data: VersionManifest = await response.json();
        setRemoteManifest(data);

        const isLocalDev =
          typeof window !== 'undefined' &&
          (window.location.hostname === 'localhost' ||
            window.location.hostname === '127.0.0.1' ||
            window.location.hostname.endsWith('.local'));

        if (data.version && data.version !== LOCAL_BUILD_METADATA.version) {
          if (isLocalDev) {
            // Em desenvolvimento local, suprime auto-reload para não travar o ambiente
            setIsStale(false);
          } else {
            // Em produção, proteção anti-loop: não recarrega mais de 1 vez a cada 2 min para a mesma versão
            const lastAttempt = sessionStorage.getItem('pwa_last_reload_attempt');
            const now = Date.now();
            if (lastAttempt) {
              try {
                const { version, ts } = JSON.parse(lastAttempt);
                if (version === data.version && now - ts < 120_000) {
                  console.warn(
                    `[VersionTelemetry] 🛑 Anti-loop ativo: reload para ${data.version} já disparado há ${Math.round((now - ts) / 1000)}s.`
                  );
                  setIsStale(false);
                  return;
                }
              } catch {
                /* parse error */
              }
            }
            setIsStale(true);
            console.warn(
              `[VersionTelemetry] ⚠️ Divergência detectada! Local: ${LOCAL_BUILD_METADATA.version} | Remoto: ${data.version}`
            );
          }
        } else {
          setIsStale(false);
        }
      }
    } catch (err) {
      // Ignora erro offline silenciosamente
    } finally {
      setLastCheck(new Date());
    }
  }, []);

  useEffect(() => {
    void checkRemoteVersion();

    // 1. Ao retornar do background no celular / alternar de aba
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        void checkRemoteVersion();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // 2. Ao restaurar de bfcache no iOS
    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        void checkRemoteVersion();
      }
    };
    window.addEventListener('pageshow', handlePageShow);

    // 3. Polling a cada 60 segundos para detecção rápida pós-deploy
    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') {
        void checkRemoteVersion();
      }
    }, 60000);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pageshow', handlePageShow);
      clearInterval(interval);
    };
  }, [checkRemoteVersion]);

  // Função atômica para limpar todos os caches, desregistrar SW e forçar reload limpo
  const purgeAndReload = useCallback(async () => {
    try {
      // 1. Desregistra todos os Service Workers ativos
      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        for (const registration of registrations) {
          await registration.unregister();
        }
      }

      // 2. Apaga todas as chaves no CacheStorage
      if ('caches' in window) {
        const keys = await caches.keys();
        await Promise.all(
          keys.filter((k) => !k.startsWith('map-tiles-')).map((k) => caches.delete(k))
        );
      }

      // 3. Limpa storage local de controle de versão (preservando trava anti-loop)
      if (remoteManifest?.version) {
        sessionStorage.setItem(
          'pwa_last_reload_attempt',
          JSON.stringify({ version: remoteManifest.version, ts: Date.now() })
        );
      }
      localStorage.removeItem('ac_app_version');
      localStorage.removeItem('pwa-update-ts');
      localStorage.removeItem('workbox-precache-v2');

      // 4. Força reload com cache buster
      const cleanUrl = window.location.origin + window.location.pathname + '?_fresh=' + Date.now();
      window.location.replace(cleanUrl);
    } catch (err) {
      console.error('[VersionTelemetry] Falha ao purgar:', err);
      window.location.reload();
    }
  }, []);

  const toggleAuditVisible = useCallback((enabled: boolean) => {
    setIsAuditVisible(enabled);
    try {
      localStorage.setItem('ac_audit_banner_enabled', enabled ? 'true' : 'false');
    } catch { /* noop */ }
  }, []);

  return {
    localManifest: LOCAL_BUILD_METADATA,
    remoteManifest,
    isStale,
    lastCheck,
    viewport,
    isAuditVisible,
    toggleAuditVisible,
    currentRouteMeta,
    purgeAndReload,
    checkRemoteVersion,
  };
}
